/**
 * Shared Design Tokens — Colors
 *
 * Single source of truth for brand colors, semantic accents, status colors,
 * button colors, and sidebar colors across web and mobile.
 *
 * Blue (#3b82f6) is the unified brand primary across both platforms.
 */

// --- Brand Color Palettes ---

export const green = {
  50: '#f0fdf4',
  100: '#dcfce7',
  200: '#bbf7d0',
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#166534',
  900: '#14532d',
}

export const blue = {
  50: '#eff6ff',
  100: '#dbeafe',
  200: '#bfdbfe',
  300: '#93c5fd',
  400: '#60a5fa',
  500: '#3b82f6',
  600: '#2563eb',
  700: '#1d4ed8',
  800: '#1e40af',
  900: '#1e3a8a',
  950: '#172554',
}

export const slate = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
}

export const brandColors = { green, blue, slate }

// --- Brand Primary / Secondary ---

export const brandPrimary = blue[500]
export const brandSecondary = green[500]

// --- Semantic Accent Colors ---

export const accentColors = {
  primary: blue[500],
  success: green[500],
  warning: '#f59e0b',
  danger: '#ef4444',
  info: blue[500],
}

// --- Status Colors (light & dark mode pairs) ---

export const statusColors = {
  light: {
    draft: { fg: '#9CA3AF', bg: '#F3F4F6' },
    pending: { fg: '#F59E0B', bg: '#FEF3C7' },
    sent: { fg: blue[500], bg: blue[100] },
    approved: { fg: green[500], bg: green[100] },
    rejected: { fg: '#EF4444', bg: '#FEE2E2' },
    completed: { fg: green[500], bg: green[100] },
    cancelled: { fg: '#6B7280', bg: '#F3F4F6' },
    inProgress: { fg: '#8B5CF6', bg: '#EDE9FE' },
    scheduled: { fg: '#06B6D4', bg: '#CFFAFE' },
    paid: { fg: green[500], bg: green[100] },
    overdue: { fg: '#EF4444', bg: '#FEE2E2' },
  },
  dark: {
    draft: { fg: '#9CA3AF', bg: '#374151' },
    pending: { fg: '#FBBF24', bg: '#78350F' },
    sent: { fg: blue[400], bg: blue[900] },
    approved: { fg: green[400], bg: green[800] },
    rejected: { fg: '#F87171', bg: '#7F1D1D' },
    completed: { fg: green[400], bg: green[800] },
    cancelled: { fg: '#9CA3AF', bg: '#374151' },
    inProgress: { fg: '#A78BFA', bg: '#4C1D95' },
    scheduled: { fg: '#22D3EE', bg: '#164E63' },
    paid: { fg: green[400], bg: green[800] },
    overdue: { fg: '#F87171', bg: '#7F1D1D' },
  },
}

// --- Button Colors ---

export const buttonColors = {
  primary: blue[500],
  secondary: '#6b7280',
  danger: '#ef4444',
  success: green[500],
}

// --- Sidebar Colors ---

export const sidebarColors = {
  bg: blue[950],
  hover: blue[500],
  active: green[300],
}

// --- Semantic Colors (light & dark mode) ---

export const semanticColors = {
  light: {
    bgPage: '#f1f5f9',
    bgSurface: '#ffffff',
    bgElevated: '#ffffff',
    bgSunken: '#f8fafc',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textTertiary: '#64748b',
    textMuted: '#94a3b8',
    textDisabled: '#cbd5e1',
    textInverse: '#ffffff',
    textLink: '#2563eb',
    borderDefault: '#e2e8f0',
    borderSubtle: '#f1f5f9',
    borderStrong: '#cbd5e1',
    borderFocus: '#3b82f6',
    inputBg: '#ffffff',
    inputBorder: '#cbd5e1',
    inputBorderFocus: '#3b82f6',
    inputPlaceholder: '#94a3b8',
  },
  dark: {
    bgPage: '#1e2a3a',
    bgSurface: '#1e293b',
    bgElevated: '#334155',
    bgSunken: '#0f172a',
    textPrimary: '#f8fafc',
    textSecondary: '#cbd5e1',
    textTertiary: '#94a3b8',
    textMuted: '#64748b',
    textDisabled: '#475569',
    textInverse: '#0f172a',
    textLink: '#60a5fa',
    borderDefault: '#334155',
    borderSubtle: '#1e293b',
    borderStrong: '#475569',
    borderFocus: '#3b82f6',
    inputBg: '#1e293b',
    inputBorder: '#4b5563',
    inputBorderFocus: '#3b82f6',
    inputPlaceholder: '#6b7280',
  },
}
